import api from './api';
import { Customer } from './customers';
import { Business } from './businesses';
import { Team } from './teams';
import { Staff } from './staffs';

export interface Invoice {
  id: number;
  invoice_number: string;
}

export interface Task {
  id: number;
  customer_id?: number;
  task_type_id?: number;
  year?: string;
  task_template_id?: number;
  quotation_id?: number;
  taskType?: {
    id: number;
    name: string;
  };
  taskTemplate?: {
    id: number;
    name: string;
  };
  customer?: Customer;
  amount?: string | number;
}

export interface Quotation {
  id: number;
  quotation_number: string;
  task_no?: string | null;
  task_description?: string | null;
  task_id?: number | null;
  date: string;
  invoice_id?: number;
  customer_id?: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'draft' | 'Pending' | 'Approved' | 'Plan' | 'Sent' | 'Approve';
  is_sent?: boolean;
  created_by?: number;
  updated_by?: number;
  created_at: string;
  updated_at: string;
  customer?: Customer;
  business?: Business;
  agent?: Staff;
  team?: Team;
  staff?: Staff;
  invoice?: Invoice;
  tasks?: Task[];
  items?: QuotationItem[];
  status_logs?: StatusLog[];
}

export interface QuotationItem {
  id?: number;
  quotation_id?: number;
  task_id?: number;
  task_type_id?: number;
  year?: number;
  task_template_id?: number;
  sub_task_template_id?: number;
  description?: string | null;
  unit?: string;
  price_per_unit?: number;
  discount?: number;
  sst_rate?: number;
  sst?: number;
  amount?: number;
  sort_order?: number;
  taskType?: {
    id: number;
    name: string;
  };
  taskTemplate?: {
    id: number;
    name: string;
  };
  subTaskTemplate?: {
    id: number;
    name: string;
    task_template_id?: number;
  };
  task?: {
    id: number;
    task_template_id?: number;
  };
}

export interface StatusLog {
  id: number;
  portal: 'admin' | 'customer';
  module: string;
  reference_id: number;
  from_status: string | null;
  to_status: string;
  updated_by?: number | null;
  updated_at: string;
  created_at: string;
  updater?: {
    id: number;
    name: string;
  };
}

export interface CreateQuotationData {
  quotation_number: string;
  task_no?: string | null;
  task_description?: string | null;
  task_id?: number | null;
  date: string;
  invoice_id?: number;
  customer_id?: number;
  type?: 'personal' | 'business';
  reference_id?: number;
  agent_id?: number;
  team_id?: number;
  staff_id?: number;
  status?: 'draft' | 'Pending' | 'Approved' | 'Plan' | 'Sent' | 'Approve';
  is_sent?: boolean;
  items?: QuotationItem[];
}

const unwrap = <T>(response: any): T => response.data?.data ?? response.data;

export const quotationService = {
  async getAll(params?: { page?: number; per_page?: number }) {
    const response = await api.get('/quotations', { params });
    return response.data;
  },

  async getById(id: number): Promise<Quotation> {
    const response = await api.get(`/quotations/${id}`);
    return unwrap<Quotation>(response);
  },

  async create(data: CreateQuotationData): Promise<Quotation> {
    const response = await api.post('/quotations', data);
    return unwrap<Quotation>(response);
  },

  async update(id: number, data: Partial<CreateQuotationData>): Promise<Quotation> {
    const response = await api.put(`/quotations/${id}`, data);
    return unwrap<Quotation>(response);
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/quotations/${id}`);
  },

  async approve(id: number): Promise<Quotation> {
    const response = await api.post(`/quotations/${id}/approve`);
    return unwrap<Quotation>(response);
  },

  async getNextNumber(date?: string): Promise<string> {
    const response = await api.get('/quotations/next-number', {
      params: date ? { date } : {},
    });
    return response.data?.data?.quotation_number || response.data?.quotation_number;
  },

  async getPdf(id: number): Promise<Blob> {
    const response = await api.get(`/quotations/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },
  async resend(id: number): Promise<Quotation> {
    const response = await api.post(`/quotations/${id}/resend`);
    return unwrap<Quotation>(response);
  },
  async import(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/quotations/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  async downloadImportTemplate() {
    const response = await api.get('/quotations/import/template', {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
  async export(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post(
      '/quotations/export',
      payload,
      { responseType: 'blob' }
    );
    return response.data as Blob;
  },
  async batchDelete(payload: { ids?: number[]; select_all?: boolean; excluded_ids?: number[]; filters?: Record<string, any> }) {
    const response = await api.post('/quotations/batch-delete', payload);
    return response.data;
  },
};
